import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {Lapp01Page} from '@/pages/components/audit-program/l-section/constants';
import {LAPP01_PAGE} from '@/pages/components/audit-program/l-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface Lapp01SummaryViewProps {
    header: AuditProgramSectionVO;
    page: Lapp01Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** Lapp01 — Summary of trade payables circularization results. */
export default function Lapp01SummaryView({
    header,
    page,
    refCode = 'Lapp01',
    values,
    onChange,
}: Lapp01SummaryViewProps) {
    const meta = {
        title: page.title || LAPP01_PAGE.title,
        asAtLabel: page.asAtLabel || LAPP01_PAGE.asAtLabel,
        metrics: page.metrics?.length ? page.metrics : LAPP01_PAGE.metrics,
        conclusionLabel: page.conclusionLabel || LAPP01_PAGE.conclusionLabel,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, maxWidth: 420}}>
                <Typography variant="body2" sx={{minWidth: 64}}>{meta.asAtLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    sx={fieldSx}
                    value={values.asAt ?? ''}
                    onChange={(e) => onChange('asAt', e.target.value)}
                />
            </Stack>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 560, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 100}}/>
                        <col style={{width: 120}}/>
                        <col style={{width: 100}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="center">No.</TableCell>
                            <TableCell sx={headSx} align="center">Value</TableCell>
                            <TableCell sx={headSx} align="center">% Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {meta.metrics.map((m) => (
                            <TableRow key={m.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{m.label}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${m.id}.no`] ?? ''}
                                        onChange={(e) => onChange(`${m.id}.no`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="right"
                                        sx={fieldSx}
                                        value={values[`${m.id}.value`] ?? ''}
                                        onChange={(e) => onChange(`${m.id}.value`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${m.id}.pct`] ?? ''}
                                        onChange={(e) => onChange(`${m.id}.pct`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.conclusionLabel}
            </Typography>
            <InputField
                type="text"
                fullWidth
                compact
                multiline
                minRows={3}
                value={values.conclusion ?? ''}
                onChange={(e) => onChange('conclusion', e.target.value)}
                sx={{maxWidth: 720}}
            />
        </Box>
    );
}
